import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ExclamationTriangleIcon,
  ClockIcon,
  CalendarDaysIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { vendorService, AutoRenewalDashboard as AutoRenewalData, Contract } from '../services/vendor.service';

const CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: 'easeOut' },
  },
};

const BANNER_STYLES = {
  danger: 'bg-danger-50 border-danger-200 dark:bg-danger-900/20 dark:border-danger-800',
  warning: 'bg-warning-50 border-warning-200 dark:bg-warning-900/20 dark:border-warning-800',
  primary: 'bg-primary-50 border-primary-200 dark:bg-primary-900/20 dark:border-primary-800',
} as const;

const DEADLINE_COLORS = {
  danger: 'text-danger-600 dark:text-danger-400',
  warning: 'text-warning-600 dark:text-warning-400',
  primary: 'text-primary-600 dark:text-primary-400',
} as const;

function getDaysRemaining(dateStr: string | undefined | null): number | null {
  if (!dateStr) {return null;}
  const now = new Date();
  const target = new Date(dateStr);
  const diff = Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return diff;
}

function ContractCard({ contract, variant }: { contract: Contract; variant: 'danger' | 'warning' | 'primary' }) {
  const daysToNotice = getDaysRemaining(contract.noticeDeadline);
  const daysToExpiry = getDaysRemaining(contract.expirationDate);

  return (
    <Link to={`/legal/contracts/${contract.id}`} className="block">
      <Card hover="default" padding="none" className={cn('transition-all duration-200 cursor-pointer border-l-4', variant === 'danger' ? 'border-l-danger-500' : variant === 'warning' ? 'border-l-warning-500' : 'border-l-primary-500')}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-3 mb-1">
                <h3 className="font-semibold text-secondary-900 dark:text-white truncate">
                  {contract.title}
                </h3>
                <span className="text-sm font-mono text-secondary-500 dark:text-secondary-400 shrink-0">
                  {contract.contractNumber}
                </span>
              </div>
              {contract.vendor && (
                <p className="text-sm text-secondary-600 dark:text-secondary-400">
                  {contract.vendor.companyLegalName}
                </p>
              )}
            </div>
            {daysToNotice !== null && (
              <span className={cn('text-sm font-bold px-3 py-1 rounded-full shrink-0', variant === 'danger' ? 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300' : variant === 'warning' ? 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300' : 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300')}>
                {daysToNotice <= 0 ? 'OVERDUE' : `${daysToNotice}d remaining`}
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2 text-sm">
            <div className="flex items-center space-x-2">
              <CalendarDaysIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400" />
              <span className="text-secondary-600 dark:text-secondary-400">Expires:</span>
              <span className="font-medium text-secondary-900 dark:text-white">
                {vendorService.formatDate(contract.expirationDate)}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <ClockIcon className={cn('w-4 h-4', DEADLINE_COLORS[variant])} />
              <span className="text-secondary-600 dark:text-secondary-400">Notice:</span>
              <span className={cn('font-medium', DEADLINE_COLORS[variant])}>
                {vendorService.formatDate(contract.noticeDeadline)}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <CurrencyDollarIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400" />
              <span className="text-secondary-600 dark:text-secondary-400">Value:</span>
              <span className="font-medium text-secondary-900 dark:text-white">
                {vendorService.formatCurrency(contract.contractValue, contract.currency)}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <ArrowPathIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400" />
              <span className="text-secondary-600 dark:text-secondary-400">Auto-term:</span>
              <span className="font-medium text-secondary-900 dark:text-white">
                {contract.autoRenewalTermMonths ? `${contract.autoRenewalTermMonths} months` : '-'}
              </span>
            </div>
          </div>

          {daysToExpiry !== null && (
            <div className="mt-3 text-xs text-secondary-500 dark:text-secondary-400">
              {daysToExpiry > 0 ? `${daysToExpiry} days until expiration` : 'Contract has expired'}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

function ContractSection({ title, contracts, variant, emptyMessage }: { title: string; contracts: Contract[]; variant: 'danger' | 'warning' | 'primary'; emptyMessage: string }) {
  return (
    <div>
      <div className={cn('p-4 rounded-t-lg border', BANNER_STYLES[variant])}>
        <h2 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center space-x-2">
          {variant === 'danger' && <ExclamationTriangleIcon className="w-5 h-5 text-danger-500" />}
          {variant === 'warning' && <ClockIcon className="w-5 h-5 text-warning-500" />}
          {variant === 'primary' && <CalendarDaysIcon className="w-5 h-5 text-primary-500" />}
          <span>{title}</span>
          <span className="text-sm font-normal text-secondary-600 dark:text-secondary-400">
            ({contracts.length})
          </span>
        </h2>
      </div>
      <div className="space-y-3 mt-3">
        {contracts.length > 0 ? (
          contracts.map((contract) => (
            <ContractCard key={contract.id} contract={contract} variant={variant} />
          ))
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-secondary-500 dark:text-secondary-400">{emptyMessage}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default function AutoRenewalDashboard() {
  const [data, setData] = useState<AutoRenewalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const dashboardData = await vendorService.getAutoRenewalDashboard();
      setData(dashboardData);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch auto-renewal dashboard:', err instanceof Error ? err.message : err);
      setError('Failed to load auto-renewal dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <motion.div
      variants={CONTAINER_VARIANTS}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Header */}
      <motion.div variants={ITEM_VARIANTS}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
              Auto-Renewal Dashboard
            </h1>
            <p className="text-secondary-600 dark:text-secondary-400 mt-1">
              Track auto-renewal contracts, notice deadlines, and upcoming renewals
            </p>
          </div>
          <Link to="/legal/contracts">
            <Button variant="outline" className="w-full sm:w-auto">
              View All Contracts
            </Button>
          </Link>
        </div>
      </motion.div>

      {loading ? (
        <motion.div variants={ITEM_VARIANTS}>
          <Card>
            <CardContent className="p-12 text-center">
              <div className="text-lg text-secondary-600 dark:text-secondary-400">
                Loading auto-renewal data...
              </div>
            </CardContent>
          </Card>
        </motion.div>
      ) : error ? (
        <motion.div variants={ITEM_VARIANTS}>
          <Card>
            <CardContent className="p-12 text-center">
              <ExclamationTriangleIcon className="w-12 h-12 text-danger-400 dark:text-danger-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                Error Loading Dashboard
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400 mb-4">
                {error}
              </p>
              <Button onClick={fetchData} variant="outline">
                Try Again
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      ) : data ? (
        <>
          {/* Summary Cards */}
          <motion.div variants={ITEM_VARIANTS}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-danger-600 dark:text-danger-400">
                    {data.summary.actionRequiredCount}
                  </div>
                  <div className="text-sm text-secondary-600 dark:text-secondary-400">
                    Action Required
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-warning-600 dark:text-warning-400">
                    {data.summary.approachingCount}
                  </div>
                  <div className="text-sm text-secondary-600 dark:text-secondary-400">
                    Approaching Deadline
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                    {data.summary.upcomingCount}
                  </div>
                  <div className="text-sm text-secondary-600 dark:text-secondary-400">
                    Upcoming Renewals
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-secondary-900 dark:text-white">
                    {data.totalAutoRenewal}
                  </div>
                  <div className="text-sm text-secondary-600 dark:text-secondary-400">
                    Total Auto-Renewal
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>

          {/* Action Required Now */}
          <motion.div variants={ITEM_VARIANTS}>
            <ContractSection
              title="Action Required Now"
              contracts={data.actionRequired}
              variant="danger"
              emptyMessage="No contracts require immediate action."
            />
          </motion.div>

          {/* Approaching Notice Deadline */}
          <motion.div variants={ITEM_VARIANTS}>
            <ContractSection
              title="Approaching Notice Deadline (30 days)"
              contracts={data.approaching}
              variant="warning"
              emptyMessage="No contracts approaching their notice deadline."
            />
          </motion.div>

          {/* Upcoming Renewals */}
          <motion.div variants={ITEM_VARIANTS}>
            <ContractSection
              title="Upcoming Renewals (31-90 days)"
              contracts={data.upcoming}
              variant="primary"
              emptyMessage="No upcoming renewals in this period."
            />
          </motion.div>
        </>
      ) : null}
    </motion.div>
  );
}
